import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import './EditProductPage.scss'

import merchantApi from '../../services/merchantApi'
import Header from '../../components/Header/Header'
import DatePicker from '../../components/DatePicker/DatePicker'
import { Grid } from '@mui/material'
import { Icon } from '@iconify/react/dist/iconify.js'
import moment from 'moment'

const moduleCode = 'catalog'

function Field({ label, optional, hint, error, children }) {
  return (
    <div className="ep-field">
      {label && (
        <label className="ep-field__label">
          {label}
          {optional && <span className="ep-field__optional">optional</span>}
        </label>
      )}
      {children}
      {error && <p className="ep-field__error">{error}</p>}
      {hint && !error && <p className="ep-field__hint">{hint}</p>}
    </div>
  )
}

function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <div className="ep-toggle">
      <div>
        <p className="ep-toggle__label">{label}</p>
        {hint && <p className="ep-toggle__hint">{hint}</p>}
      </div>
      <label className="ep-switch">
        <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
        <span className="ep-switch__track" />
      </label>
    </div>
  )
}

function ModifierCard({ group }) {
  const options = group.options || []
  const typeLabel =
    group.type === 'single' ? 'Single choice' :
      group.type === 'multiple' ? 'Multiple choice' : 'Free text'

  return (
    <div className="ep-modifier-card">
      <div className="ep-modifier-card__head">
        <div className="ep-modifier-card__title-container">
          <h4 className="ep-modifier-card__title">{group.title}</h4>
          <span className={`ep-modifier-card__badge ep-modifier-card__badge--${group.required ? 'required' : 'optional'}`}>
            {group.required ? 'Required' : 'Optional'}
          </span>
          <span className={`ep-modifier-card__badge ep-modifier-card__badge--selection`}>
            {group.type === 'single' ? 'Single choice' : group.type === 'multiple' ? 'Multiple choice' : 'Free text'}
          </span>
        </div>
        {
          group.read_only && (
            <div className="ep-modifier-card__readonly">
              <Icon icon="ph:lock-simple" height={12} />
              Read-only
            </div>
          )
        }
      </div>

      {options.length > 0 && (
        <div className="ep-modifier-card__options">
          {options.map((opt) => (
            <div key={opt.id || opt.name} className="ep-modifier-option">
              <span>{opt.name}</span>
              <div className="ep-modifier-option__price">
                {
                  opt.price === 0 ?
                    "Included" : `+ RM${Number(opt.price || 0).toFixed(2)}`
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const getPayload = (response) => response?.data?.data || response?.data || response?.product || response

const getTranslationValue = (product, keys) => {
  const translations = product?.translations || product?.translation || []
  const firstTranslation = Array.isArray(translations) ? translations[0] : translations

  for (const key of keys) {
    if (product?.[key]) return product[key]
    if (product?.data?.[key]) return product.data[key]
    if (firstTranslation?.[key]) return firstTranslation[key]
  }

  return ''
}

const normalizeProduct = (product) => {
  const category =
    product?.category?.name ||
    product?.category_name ||
    product?.main_category?.name ||
    product?.categories?.[0]?.description?.name ||
    product?.product_category?.name ||
    'Uncategorised'

  const price = product?.normal_price ?? product?.price ?? ''
  const discountValue = product?.discount_value ?? ''
  return {
    ...product,
    id: product?.id,
    name:
      getTranslationValue(product, ['name', 'title', 'product_name']) ||
      product?.sku ||
      `Product #${product?.id}`,
    posName: product?.pos_name || product?.pos_product_name || product?.sku || '',
    short_description: product?.description?.description || '',
    price: price === null || price === undefined ? '' : String(price),
    discountValue: discountValue === null || discountValue === undefined ? '' : String(discountValue),
    image:
      product?.image_url ||
      product?.thumbnail_image ||
      product?.image ||
      product?.media?.[0]?.url ||
      product?.images?.[0]?.url || null,
    category,
    status: Number(product?.status ?? 1),
    visibility: Number(product?.visibility ?? 1),
    featured: Boolean(product?.featured),
    containsAlcohol: Boolean(product?.contains_alcohol),
    description: product?.description || '',
  }
}

const normalizeModifierGroups = (product) => {
  const groups = product?.modifier_groups || product?.modifiers || product?.option_groups || []

  if (!Array.isArray(groups) || groups.length === 0) {
    return []
  }

  return groups.map((group, index) => ({
    id: group.id || index,
    title: group.name || group.title || `Modifier group ${index + 1}`,
    type: group.selection_type || (group.max_selection === 1 ? 'single' : 'multiple'),
    required: Boolean(group.required || group.is_required),
    read_only: Boolean(group.read_only || group.is_read_only),
    options: (group.options || group.items || []).map((option, optionIndex) => ({
      id: option.id || optionIndex,
      name: option.name || option.title || `Option ${optionIndex + 1}`,
      price: option.price_adjustment || option.amount || 0,
    })),
  }))
}

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null
  const number = Number(value)
  return Number.isNaN(number) ? null : number
}

export default function EditProductPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [product, setProduct] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [basePrice, setBasePrice] = useState('')
  const [discountValue, setDiscountValue] = useState('')
  const [availableOnMenu, setAvailableOnMenu] = useState(false)
  const [bestSeller, setBestSeller] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [fieldError, setFieldError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')

  // New states for discount and validity
  const [hasDiscount, setHasDiscount] = useState(false)
  const [hasValidity, setHasValidity] = useState(false)
  const [validFrom, setValidFrom] = useState(null)
  const [validUntil, setValidUntil] = useState(null)

  // New state for uploaded image
  const [newImage, setNewImage] = useState(null)

  const modifierGroups = useMemo(() => normalizeModifierGroups(product || {}), [product])

  const loadProduct = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await merchantApi.products.show(id)
      const normalized = normalizeProduct(getPayload(response))
      
      setProduct(normalized)
      setName(normalized.name)
      setDescription(normalized.short_description)
      setBasePrice(normalized.price)
      setDiscountValue(normalized.discountValue)
      setAvailableOnMenu(Boolean(normalized.available_on_menu))
      setBestSeller(Boolean(normalized.best_seller_badge))
      setHasDiscount(Boolean(normalized.has_discount))
      setHasValidity(Boolean(normalized.has_discount_validity))
      setValidFrom(normalized.discount_start_at ? moment(normalized.discount_start_at).toDate() : null)
      setValidUntil(normalized.discount_end_at ? moment(normalized.discount_end_at).toDate() : null)
      setNewImage(null) // Reset new image when product is loaded
    } catch (err) {
      setError(err?.message || 'Unable to load product.')
    } finally {
      setLoading(false)
    }
  }

  const loadUserInfo = async () => {
    try {
      const response = await merchantApi.auth.me()
      if (!response?.data?.permissions?.includes(`edit-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }
    } catch (err) {
      setError(err?.message || 'Failed to load user info.')
    }
  }

  useEffect(() => {
    loadUserInfo()
  }, [])

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a JPG, JPEG, PNG, or WEBP file.')
      return
    }

    // Convert file to base64
    const reader = new FileReader()
    reader.onloadend = () => {
      setNewImage({ file: file, base64: reader.result }) // This will be a base64 string
      setError('')
    }
    reader.onerror = () => {
      setError('Failed to read the file.')
    }
    reader.readAsDataURL(file)
  }

  useEffect(() => {
    loadProduct()
  }, [id])

  const handleSave = async () => {
    try {
      setSaving(true)
      setError('')
      setSuccessMessage('')

      const payload = {
        // featured: availableOnMenu ? true : false,
        name: name,
        short_description: description,
        best_seller_badge: bestSeller,
        status: availableOnMenu ? 1 : 0,
        visibility: availableOnMenu ? 1 : 0,
        available_on_menu: availableOnMenu ? true : false,
        normal_price: toNumberOrNull(basePrice),
        price: toNumberOrNull(((basePrice - 0) - (discountValue || 0)).toFixed(2)),
        has_discount: hasDiscount,
        has_discount_validity: hasValidity,
        discount_start_at: hasValidity ? (validFrom ? moment(validFrom).format('YYYY-MM-DD') : null) : null,
        discount_end_at: hasValidity ? (validUntil ? moment(validUntil).format('YYYY-MM-DD') : null) : null,
        discount_price: toNumberOrNull(((basePrice - 0) - (discountValue || 0)).toFixed(2)),
        discount_value: toNumberOrNull(discountValue),
      }

      if (newImage) {
        await merchantApi.products.updateImage(id, newImage.file)
      }

      if (!newImage && !product?.image) {
        await merchantApi.products.deleteImage(id)
      }

      await setTimeout(async () => {

        try {
          await merchantApi.products.update(id, payload)
          setSuccessMessage('Product updated successfully.')
          setFieldError(null)
          setError(null)
          await loadProduct()
        } catch (err) {
          console.log("err", err)
          setFieldError(err?.data?.errors || null)
          setError(err?.data?.message || err?.message || 'Unable to update product.')
        } finally {
          setSaving(false)
        }
      }, newImage ? 2000 : 0)
    } catch (err) {
      console.log("err", err)
      setFieldError(err?.data?.errors || null)
      setError(err?.data?.message || err?.message || 'Unable to update product.')
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="ep-layout">
        <main className="ep-page">
          <div className="ep-card">
            <div className="ep-card__body">Loading product...</div>
          </div>
        </main>
      </div>
    )
  }

  if (error && !product) {
    return (
      <div className="ep-layout">
        <main className="ep-page">
          <div className="ep-topbar">
            <div className="ep-topbar__breadcrumb">
              <Link to="/catalog" className="ep-topbar__back">
                <Icon icon="ph:chevron-left" width={16} /> Back
              </Link>
            </div>
          </div>
          <div className="ep-card">
            <div className="ep-card__body">{error}</div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="ep-layout">
      <main className="ep-page">
        <div className="ep-topbar">
          <Header
            items={[
              { label: 'Product', to: '/' },
              { label: 'Edit product', current: true },
            ]}
            showBack={true}
            onBack={() => navigate("/catalog")}
            backLabel="Back"
          />
          <div className="ep-topbar__actions">
            <button className="ep-btn ep-btn--outline" type="button" onClick={() => navigate('/catalog')} disabled={saving}>
              Cancel
            </button>
            <button className="ep-btn ep-btn--primary" type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="ep-card-error" style={{ marginBottom: 16 }}>
            <div className="api-error">{error}</div>
          </div>
        )}

        {successMessage && (
          <div className="ep-card-success" style={{ marginBottom: 16 }}>
            <div className="">{successMessage}</div>
          </div>
        )}

        <div className="">
          <Grid container spacing={2}>
            <Grid item lg={8} md={12} sm={12} xs={12}>
              <div className="ep-main">
                <section className="ep-card">
                  <div className="ep-card__header">
                    <h3>Basic information</h3>
                    <span className="catalog-card__tag">
                      {product?.category || 'Uncategorised'}
                    </span>
                  </div>
                  <div className="ep-card__body">
                    <div className="ep-image">
                      {
                        (product?.image || newImage) && (
                          <>
                            <img
                              src={newImage?.base64 || product?.image}
                              alt={name || 'Product'}
                            // onError={(event) => {
                            //   event.currentTarget.src = FALLBACK_IMAGE
                            // }}
                            />
                            <button className="ep-image__remove" aria-label="Remove image" type="button" onClick={() => {
                              if (newImage) {
                                setNewImage(null)
                              } else {
                                setProduct({ ...product, image: null })
                              }
                            }}>
                              <Icon icon="ph:x" width={16} />
                            </button>
                          </>
                        )
                      }
                      {
                        !product?.image && !newImage && (
                          <div className="ep-image__placeholder">
                            <input
                              type="file"
                              id="product-image-input"
                              className="ep-image__input"
                              accept=".jpg,.jpeg,.png,.webp"
                              onChange={handleFileChange}
                            />
                            <label htmlFor="product-image-input" className="ep-image__upload-label">
                              Click to upload image
                            </label>
                          </div>
                        )
                      }

                    </div>
                    <Grid container spacing={2}>
                      <Grid item sm={12} xs={12}>
                        <Field label="Product name" hint="Shown as the product title in the menu" error={fieldError?.name || fieldError?.title || fieldError?.product_name}>
                          <input
                            className="ep-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                          />
                        </Field>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field label="Name in POS" hint="Synced from POS — read only">
                          <input
                            className="ep-input ep-input--readonly"
                            type="text"
                            value={product?.description?.name_alternative || ''}
                            readOnly
                          />
                        </Field>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field label="Product sku" hint="Stock keeping unit" error={fieldError?.name || fieldError?.title || fieldError?.product_name}>
                          <input
                            className="ep-input ep-input--readonly"
                            type="text"
                            value={product?.sku || ''}
                            readOnly
                          />
                        </Field>
                      </Grid>
                      <Grid item sm={12} xs={12}>
                        <Field label="Short description" optional hint="Help customers understand the product">
                          <textarea
                            className="ep-textarea"
                            rows={5}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                          />
                        </Field>
                      </Grid>
                    </Grid>
                  </div>
                </section>

                <section className="ep-card">
                  <div className="ep-card__header"><h3>Pricing</h3></div>
                  <div className="ep-card__body">
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <Field label="Base price" hint={`Price from POS: RM${basePrice}`}>
                          <input
                            className="ep-input"
                            type="text"
                            inputMode="decimal"
                            value={basePrice}
                            onChange={(e) => setBasePrice(e.target.value)}
                            prefix='RM'
                            disabled={true}
                          />
                        </Field>
                      </Grid>
                      {/* <Grid item sm={6} xs={0}></Grid>
                      <Grid item sm={6} xs={12}>
                        <div className="ep-radio-group">
                          <p className="ep-radio-group__label">With discount?</p>
                          <div className="cdp-modal__radio-row">
                            <label className="cdp-modal__radio">
                              <input type="radio" name="discount" value="yes" checked={hasDiscount} onChange={() => setHasDiscount(true)} />
                              <span className="cdp-modal__radio-circle" />
                              Yes
                            </label>
                            <label className="cdp-modal__radio">
                              <input type="radio" name="discount" value="no" checked={!hasDiscount} onChange={() => setHasDiscount(false)} />
                              <span className="cdp-modal__radio-circle" />
                              No
                            </label>
                          </div>
                        </div>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        {hasDiscount && (
                          <Field label="Discount price">
                            <input
                              className="ep-input"
                              type="text"
                              inputMode="decimal"
                              value={discountValue}
                              onChange={(e) => setDiscountValue(e.target.value)}
                              prefix='RM'
                            />
                          </Field>
                        )}
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <div className="ep-radio-group">
                          <p className="ep-radio-group__label">With validity?</p>
                          <div className="cdp-modal__radio-row">
                            <label className="cdp-modal__radio">
                              <input type="radio" name="validity" value="yes" checked={hasValidity} onChange={() => setHasValidity(true)} />
                              <span className="cdp-modal__radio-circle" />
                              Yes
                            </label>
                            <label className="cdp-modal__radio">
                              <input type="radio" name="validity" value="no" checked={!hasValidity} onChange={() => setHasValidity(false)} />
                              <span className="cdp-modal__radio-circle" />
                              No
                            </label>
                          </div>
                        </div>
                      </Grid>
                      <Grid item sm={6} xs={12}></Grid>
                      {hasValidity && (
                        <>
                          <Grid item sm={6} xs={12}>
                            <div className="ep-date-picker-item">
                              <Field label="Valid from" >
                                <DatePicker
                                  value={validFrom}
                                  onChange={setValidFrom}
                                  placeholder="dd/mm/yyyy"
                                />
                              </Field>
                            </div>
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <div className="ep-date-picker-item">
                              <Field label="Valid until" hint={'Leave blank for no expiry'}>
                                <DatePicker
                                  value={validUntil}
                                  onChange={setValidUntil}
                                  placeholder="dd/mm/yyyy"
                                />
                              </Field>
                            </div>
                          </Grid>
                        </>
                      )} */}
                    </Grid>
                  </div>
                </section>

                <section className="ep-card">
                  <div className="ep-card__header">
                    <h3>Modifier</h3>
                    <div className={`ep-card__header_badge`}>
                      <Icon icon={'ph:lock-simple'} height={12} />
                      Managed by POS
                    </div>
                  </div>
                  <div className="ep-card__body">
                    {modifierGroups.length > 0 ? (
                      <div className="ep-modifier-list">
                        {modifierGroups.map((group) => (
                          <ModifierCard key={group.id} group={group} />
                        ))}
                      </div>
                    ) : (
                      <p className="ep-field__hint">No modifier groups returned by the API.</p>
                    )}
                  </div>
                </section>
              </div>
            </Grid>
            <Grid item lg={4} md={12} sm={12} xs={12}>
              {/* <aside className="ep-sidebar"> */}
              <section className="ep-card">
                <div className="ep-card__header"><h3>Display settings</h3></div>
                <div className="ep-card__body">
                  <Toggle
                    label="Available on menu"
                    hint="Visible and orderable by customers"
                    checked={availableOnMenu}
                    onChange={(e) => setAvailableOnMenu(e.target.checked)}
                    disabled={saving}
                  />
                  <Toggle
                    label="Best seller badge"
                    checked={bestSeller}
                    onChange={(e) => setBestSeller(e.target.checked)}
                    disabled={saving}
                  />
                </div>
              </section>
              {/* </aside> */}
            </Grid>
          </Grid>



        </div>
      </main>
    </div>
  )
}
